import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useApi } from "../../../hooks/useApi";
import { buildBlogUrl, resolveBlogImageUrl } from "../../blog/blogApi";
import {
  excerptFromHtml,
  excerptFromMarkdown,
  markdownToBlocks,
  sanitizeRichHtml,
  slugify,
  stripHtml,
  stripMarkdown,
} from "../../blog/blogUtils";
import "./AdminBlogEditor.css";

const DEFAULT_AUTHOR = "Команда Энилекс";
const EMPTY_FORM = { id: null, title: "", slug: "", description: "", content: "", content_format: "markdown", content_type: "Статьи", category: "", tags: [], author: DEFAULT_AUTHOR, og_image: "", reading_time: "", status: "draft" };
const CATEGORY_OPTIONS = ["Операции на маркетплейсах", "Аналитика", "Реклама", "SEO", "Ценообразование", "Логистика", "Отзывы", "Управление товарами", "Автоматизация"];
const CONTENT_TYPE_OPTIONS = ["Новости", "Статьи", "Инструкции"];
const STATUS_LABELS = { draft: "Черновик", published: "Опубликовано", archived: "Архив" };
const TAG_MAP = new Map([["analytics", "аналитика"], ["advertising", "реклама"], ["ads", "реклама"], ["reviews", "отзывы"], ["review", "отзывы"], ["pricing", "ценообразование"], ["logistics", "логистика"], ["product-management", "управление товарами"], ["dashboard", "дашборд"], ["owner", "руководитель"], ["ai", "автоматизация"], ["automation", "автоматизация"], ["unit economics", "юнит-экономика"], ["unit-economics", "юнит-экономика"], ["unit", "юнит-экономика"], ["sku", "SKU"], ["ozon", "Ozon"], ["wildberries", "Wildberries"], ["seo", "SEO"], ["cpc", "CPC"], ["cpm", "CPM"], ["fbo", "FBO"], ["fbs", "FBS"], ["excel", "Excel"]]);
const TOOLS = [{ type: "formatBlock", level: 1, value: "h1", label: "H1" }, { type: "formatBlock", level: 2, value: "h2", label: "H2" }, { type: "formatBlock", level: 3, value: "h3", label: "H3" }, { type: "formatBlock", level: 4, value: "h4", label: "H4" }, { type: "formatBlock", level: 5, value: "h5", label: "H5" }, { type: "bold", label: "Ж" }, { type: "italic", label: "К" }, { type: "insertUnorderedList", label: "Список" }, { type: "insertOrderedList", label: "1." }, { type: "quote", label: "Цитата" }, { type: "link", label: "Ссылка" }];

function normalizeTag(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  return TAG_MAP.get(raw.toLowerCase()) || raw;
}

function normalizeForm(data) {
  return {
    id: data.id || null,
    title: data.title || "",
    slug: data.slug || "",
    description: data.description || "",
    content: data.content || "",
    content_format: data.content_format || data.contentFormat || "markdown",
    content_type: data.content_type || data.contentType || "Статьи",
    category: data.category || "",
    tags: Array.isArray(data.tags) ? data.tags.map(normalizeTag).filter(Boolean) : [],
    author: data.author || DEFAULT_AUTHOR,
    og_image: data.og_image || "",
    reading_time: data.reading_time ? String(data.reading_time) : "",
    status: data.status || "draft",
  };
}

function countWords(content, format) {
  const plain = format === "html" ? stripHtml(content) : stripMarkdown(content);
  return plain.split(/\s+/).filter(Boolean).length;
}

function seoState(length, goodMin, goodMax, warnMax) {
  if (!length) return "bad";
  if (length >= goodMin && length <= goodMax) return "good";
  if (length <= warnMax) return "warn";
  return "bad";
}

function PreviewBlock({ block }) {
  if (block.type === "heading") {
    const Tag = block.level === 3 ? "h3" : "h2";
    return <Tag>{block.text}</Tag>;
  }
  if (block.type === "list") {
    const Tag = block.ordered ? "ol" : "ul";
    return <Tag>{block.items.map((item) => <li key={item}>{item}</li>)}</Tag>;
  }
  if (block.type === "code") {
    return <pre><code>{block.code}</code></pre>;
  }
  return <p>{block.text}</p>;
}

function AdminBlogEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { request, authFetch, API_BASE_URL, showStatus } = useApi();
  const isNew = !id || id === "new";
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [metaPanelOpen, setMetaPanelOpen] = useState(true);
  const [tagValue, setTagValue] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const ogImageInputRef = useRef(null);
  const inlineImageInputRef = useRef(null);
  const markdownInputRef = useRef(null);
  const htmlEditorRef = useRef(null);

  useEffect(() => {
    let active = true;
    async function loadArticle() {
      if (isNew) {
        setForm(EMPTY_FORM);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const data = await request(`/api/v1/admin/blog/articles/${id}`);
        if (!active) return;
        setForm(normalizeForm(data));
        setSlugTouched(Boolean(data.slug));
      } catch (error) {
        if (active) showStatus("error", error.message || "Не удалось загрузить статью");
      } finally {
        if (active) setLoading(false);
      }
    }
    loadArticle();
    return () => { active = false; };
  }, [id, isNew, request, showStatus]);

  useEffect(() => {
    if (form.content_format === "html" && htmlEditorRef.current) {
      const html = sanitizeRichHtml(form.content || "");
      if (htmlEditorRef.current.innerHTML !== html) htmlEditorRef.current.innerHTML = html;
    }
  }, [form.content, form.content_format]);

  const previewBlocks = useMemo(() => markdownToBlocks(form.content), [form.content]);
  const previewDescription = form.description.trim() || (form.content_format === "html" ? excerptFromHtml(form.content, 160) : excerptFromMarkdown(form.content, 160));
  const wordCount = useMemo(() => countWords(form.content, form.content_format), [form.content, form.content_format]);
  const articleUrl = buildBlogUrl(`/blog/${form.slug || "slug"}`);
  const previewImage = resolveBlogImageUrl(form.og_image || "");
  const titleState = seoState(form.title.trim().length, 30, 60, 70);
  const descriptionState = seoState(previewDescription.length, 120, 160, 175);
  const slugState = form.slug.trim() ? "good" : "bad";

  function updateField(field, value) { setForm((current) => ({ ...current, [field]: value })); }
  function syncHtml() { if (form.content_format === "html" && htmlEditorRef.current) updateField("content", htmlEditorRef.current.innerHTML); }
  function handleTitleChange(value) { setForm((current) => ({ ...current, title: value, slug: !slugTouched && isNew ? slugify(value) : current.slug })); }
  function handleAddTag() { const tag = normalizeTag(tagValue); if (!tag || form.tags.includes(tag)) return setTagValue(""); setForm((current) => ({ ...current, tags: [...current.tags, tag] })); setTagValue(""); }
  function handleRemoveTag(tag) { setForm((current) => ({ ...current, tags: current.tags.filter((item) => item !== tag) })); }

  function validate(payload) {
    if (!payload.title) return "Укажите title";
    if (!payload.slug) return "Укажите slug";
    if (!payload.description) return "Укажите description";
    if (!payload.category) return "Выберите категорию";
    if (!payload.content.trim()) return "Добавьте content";
    return null;
  }

  function buildPayload(status) {
    return { slug: (form.slug || slugify(form.title)).trim(), title: form.title.trim(), description: form.description.trim(), content: form.content, content_format: form.content_format, content_type: form.content_type, category: form.category || null, tags: form.tags, author: form.author.trim() || DEFAULT_AUTHOR, og_image: form.og_image.trim() || null, reading_time: form.reading_time ? Number(form.reading_time) : null, status };
  }

  async function saveArticle(nextStatus) {
    syncHtml();
    const payload = buildPayload(nextStatus);
    const errorText = validate(payload);
    if (errorText) return showStatus("error", errorText), null;
    setSaving(true);
    try {
      if (!form.id) {
        const created = await request("/api/v1/admin/blog/articles", { method: "POST", body: JSON.stringify(payload) });
        setForm(normalizeForm(created));
        setSlugTouched(true);
        return created;
      }
      const updated = await request(`/api/v1/admin/blog/articles/${form.id}`, { method: "PUT", body: JSON.stringify(payload) });
      setForm((current) => normalizeForm({ ...current, ...updated }));
      return updated;
    } catch (error) {
      showStatus("error", error.message || "Не удалось сохранить статью");
      return null;
    } finally {
      setSaving(false);
    }
  }

  async function handleSave() {
    const nextStatus = form.status === "archived" ? "archived" : "draft";
    const saved = await saveArticle(nextStatus);
    if (!saved) return;
    showStatus("success", nextStatus === "archived" ? "Статья сохранена в архив" : "Черновик сохранен");
    if (!form.id && saved.id) navigate(`/admin/blog/${saved.id}/edit`, { replace: true });
  }

  async function handlePublish() {
    const saved = await saveArticle("draft");
    if (!saved) return;
    try {
      const published = await request(`/api/v1/admin/blog/articles/${saved.id || form.id}/publish`, { method: "POST" });
      setForm((current) => normalizeForm({ ...current, ...published, status: "published" }));
      showStatus("success", "Статья опубликована");
      if (!form.id && saved.id) navigate(`/admin/blog/${saved.id}/edit`, { replace: true });
    } catch (error) {
      showStatus("error", error.message || "Не удалось опубликовать статью");
    }
  }

  async function handleUnpublish() {
    if (!form.id) return;
    try {
      const draft = await request(`/api/v1/admin/blog/articles/${form.id}/unpublish`, { method: "POST" });
      setForm((current) => normalizeForm({ ...current, ...draft, status: "draft" }));
      showStatus("success", "Статья снята с публикации");
    } catch (error) {
      showStatus("error", error.message || "Не удалось снять публикацию");
    }
  }

  async function handleDelete() {
    if (!form.id || !window.confirm(`Удалить статью "${form.title || "без названия"}"?`)) return;
    try {
      await request(`/api/v1/admin/blog/articles/${form.id}`, { method: "DELETE" });
      showStatus("success", "Статья удалена");
      navigate("/admin/blog");
    } catch (error) {
      showStatus("error", error.message || "Не удалось удалить статью");
    }
  }

  async function uploadImage(file) {
    const body = new FormData();
    body.append("file", file);
    // authFetch сам разбирает JSON и бросает исключение на !ok: на выходе уже
    // {url, filename}, а не Response. Проверка response.ok здесь срабатывала
    // всегда — файл уходил на сервер, но UI рапортовал об ошибке
    return authFetch(`${API_BASE_URL}/api/v1/admin/blog/upload-image`, { method: "POST", body });
  }

  async function handleOgImageUpload(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const data = await uploadImage(file);
      updateField("og_image", data.url || "");
      showStatus("success", "Изображение для сниппета загружено");
    } catch (error) {
      showStatus("error", error.message || "Не удалось загрузить изображение");
    } finally {
      event.target.value = "";
    }
  }

  function wrapMarkdown(before, after = before, placeholder = "текст") {
    const area = markdownInputRef.current;
    if (!area) return;
    const start = area.selectionStart;
    const end = area.selectionEnd;
    const selected = area.value.slice(start, end) || placeholder;
    updateField("content", area.value.slice(0, start) + before + selected + after + area.value.slice(end));
  }

  function insertMarkdown(prefix, placeholder = "Текст") {
    const area = markdownInputRef.current;
    if (!area) return;
    const start = area.selectionStart;
    const end = area.selectionEnd;
    const selected = area.value.slice(start, end) || placeholder;
    updateField("content", area.value.slice(0, start) + prefix + selected + area.value.slice(end));
  }

  function applyHtmlCommand(command, value = null) {
    if (form.content_format !== "html" || !htmlEditorRef.current) return;
    htmlEditorRef.current.focus();
    document.execCommand(command, false, value);
    syncHtml();
  }

  function handleTool(tool) {
    if (form.content_format === "html") {
      if (tool.type === "formatBlock") return applyHtmlCommand("formatBlock", tool.value);
      if (tool.type === "link") {
        const href = window.prompt("Ссылка");
        if (href) applyHtmlCommand("createLink", href);
        return;
      }
      if (tool.type === "quote") return applyHtmlCommand("formatBlock", "blockquote");
      return applyHtmlCommand(tool.type, tool.value || null);
    }
    if (tool.type === "formatBlock") return wrapMarkdown(`${"#".repeat(tool.level)} `, "", "Заголовок");
    if (tool.type === "bold") return wrapMarkdown("**");
    if (tool.type === "italic") return wrapMarkdown("*");
    if (tool.type === "insertUnorderedList") return insertMarkdown("- ");
    if (tool.type === "insertOrderedList") return insertMarkdown("1. ");
    if (tool.type === "quote") return insertMarkdown("> ");
    if (tool.type === "link") {
      const href = window.prompt("Ссылка");
      if (href) wrapMarkdown("[", `](${href})`, "текст ссылки");
    }
  }

  async function handleInlineImageUpload(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const data = await uploadImage(file);
      const url = data.url || "";
      if (!url) throw new Error("Не получен URL изображения");
      if (form.content_format === "html") {
        applyHtmlCommand("insertHTML", `<figure><img src="${url}" alt="${form.title || "Иллюстрация"}" /><figcaption>Инфографика внутри статьи</figcaption></figure>`);
      } else {
        wrapMarkdown(`![Инфографика](${url})\n\n`, "", "");
      }
      showStatus("success", "Изображение вставлено в контент");
    } catch (error) {
      showStatus("error", error.message || "Не удалось вставить изображение");
    } finally {
      event.target.value = "";
    }
  }

  function handleVideoInsert() {
    const url = window.prompt("Ссылка на видео или embed URL");
    if (!url) return;
    if (form.content_format === "html") return applyHtmlCommand("insertHTML", `<figure><iframe src="${url}" title="Видео" frameborder="0"></iframe><figcaption>Видео внутри статьи</figcaption></figure>`);
    wrapMarkdown(`\n[Видео](${url})\n\n`, "", "");
  }

  return (
    <div className="blog-editor-page">
      <div className="blog-editor-page__topbar">
        <Link to="/admin/blog" className="blog-editor-page__back">Назад к списку</Link>
        <h1 className="blog-editor-page__title">{isNew ? "Новая статья" : form.title || "Редактор статьи"}</h1>
        <div className="blog-editor-page__actions">
          <span className={`blog-editor-page__status blog-editor-page__status--${form.status}`}>{STATUS_LABELS[form.status] || form.status}</span>
          {form.id && form.status === "published" ? <button type="button" className="admin-blog-button admin-blog-button--ghost" onClick={handleUnpublish} disabled={saving}>Снять</button> : null}
          {form.id ? <button type="button" className="admin-blog-button admin-blog-button--danger" onClick={handleDelete} disabled={saving}>Удалить</button> : null}
          <button type="button" className="admin-blog-button admin-blog-button--ghost" onClick={handleSave} disabled={saving}>{saving ? "Сохранение..." : "Сохранить черновик"}</button>
          <button type="button" className="admin-blog-button admin-blog-button--primary" onClick={handlePublish} disabled={saving}>Опубликовать</button>
        </div>
      </div>

      {loading ? <div className="blog-editor-page__state">Загрузка статьи...</div> : <>
        <div className="blog-editor">
          <section className="blog-editor__pane">
            <div className="blog-editor__pane-header">
              <span>Контент ({form.content_format === "html" ? "HTML" : "Markdown"})</span>
              <div className="blog-editor__switcher" role="tablist" aria-label="Режим контента">
                <button type="button" className={`blog-editor__switch${form.content_format === "markdown" ? " active" : ""}`} onClick={() => updateField("content_format", "markdown")}>Markdown</button>
                <button type="button" className={`blog-editor__switch${form.content_format === "html" ? " active" : ""}`} onClick={() => updateField("content_format", "html")}>HTML</button>
              </div>
            </div>
            <div className="blog-editor__toolbar" aria-label="Панель форматирования">
              {TOOLS.map((tool) => <button key={`${tool.type}-${tool.label}`} type="button" className="blog-editor__tool" onClick={() => handleTool(tool)}>{tool.label}</button>)}
              <button type="button" className="blog-editor__tool" onClick={() => inlineImageInputRef.current?.click()}>Фото</button>
              <button type="button" className="blog-editor__tool" onClick={handleVideoInsert}>Видео</button>
            </div>
            {form.content_format === "markdown" ? (
              <textarea ref={markdownInputRef} className="blog-editor__textarea" value={form.content} onChange={(event) => updateField("content", event.target.value)} placeholder="# Заголовок&#10;&#10;Текст статьи..." />
            ) : (
              <div ref={htmlEditorRef} className="blog-editor__richtext" contentEditable suppressContentEditableWarning onInput={syncHtml} onBlur={syncHtml} />
            )}
            <input ref={inlineImageInputRef} type="file" accept="image/*" className="blog-meta-field__file" onChange={handleInlineImageUpload} />
          </section>

          <section className="blog-editor__pane">
            <div className="blog-editor__pane-header">Предпросмотр</div>
            <div className="blog-editor__preview">
              {form.content_format === "html" ? (form.content ? <div dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(form.content) }} /> : <div className="blog-editor__preview-empty">Предпросмотр появится после ввода HTML-контента.</div>) : (previewBlocks.length ? previewBlocks.map((block, index) => <PreviewBlock key={`${block.type}-${index}`} block={block} />) : <div className="blog-editor__preview-empty">Предпросмотр появится после ввода markdown.</div>)}
            </div>
          </section>
        </div>

        <div className="blog-meta-panel">
          <button type="button" className="blog-meta-panel__toggle" onClick={() => setMetaPanelOpen((current) => !current)}>
            <span>SEO и метаданные</span>
            <span className={`blog-meta-panel__toggle-icon ${metaPanelOpen ? "blog-meta-panel__toggle-icon--open" : ""}`}>^</span>
          </button>
          {metaPanelOpen ? <div className="blog-meta-panel__body">
            <div className="blog-meta-field">
              <label className="blog-meta-field__label" htmlFor="blog-title">Title</label>
              <input id="blog-title" className="blog-meta-field__input" value={form.title} onChange={(event) => handleTitleChange(event.target.value)} />
              <div className={`blog-meta-field__counter blog-meta-field__counter--${titleState}`}>{form.title.trim().length}/60</div>
            </div>
            <div className="blog-meta-field">
              <label className="blog-meta-field__label" htmlFor="blog-slug">Slug</label>
              <input id="blog-slug" className="blog-meta-field__input" value={form.slug} readOnly={!isNew} onChange={(event) => { setSlugTouched(true); updateField("slug", slugify(event.target.value)); }} />
              <div className="blog-meta-field__hint">{isNew ? `URL: /blog/${form.slug || "slug"}` : "Slug не обновляется текущим API после создания."}</div>
            </div>
            <div className="blog-meta-field blog-meta-field--full">
              <label className="blog-meta-field__label" htmlFor="blog-description">Description</label>
              <textarea id="blog-description" className="blog-meta-field__input" rows={3} value={form.description} onChange={(event) => updateField("description", event.target.value)} />
              <div className={`blog-meta-field__counter blog-meta-field__counter--${descriptionState}`}>{previewDescription.length}/160</div>
            </div>
            <div className="blog-meta-field">
              <label className="blog-meta-field__label" htmlFor="blog-content-type">Раздел</label>
              <select id="blog-content-type" className="blog-meta-field__input" value={form.content_type} onChange={(event) => updateField("content_type", event.target.value)}>{CONTENT_TYPE_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}</select>
            </div>
            <div className="blog-meta-field">
              <label className="blog-meta-field__label" htmlFor="blog-category">Категория</label>
              <select id="blog-category" className="blog-meta-field__input" value={form.category} onChange={(event) => updateField("category", event.target.value)}>
                <option value="">Выберите категорию</option>
                {CATEGORY_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
              </select>
            </div>
            <div className="blog-meta-field">
              <label className="blog-meta-field__label" htmlFor="blog-author">Автор</label>
              <input id="blog-author" className="blog-meta-field__input" value={form.author} readOnly={!isNew} onChange={(event) => updateField("author", event.target.value)} />
              {!isNew ? <div className="blog-meta-field__hint">Автор фиксируется при создании статьи.</div> : null}
            </div>
            <div className="blog-meta-field">
              <label className="blog-meta-field__label" htmlFor="blog-reading-time">Время чтения</label>
              <div className="blog-meta-field__row">
                <input id="blog-reading-time" className="blog-meta-field__input" type="number" min="1" value={form.reading_time} onChange={(event) => updateField("reading_time", event.target.value)} />
                <span className="blog-meta-field__hint">{wordCount} слов</span>
              </div>
            </div>
            <div className="blog-meta-field">
              <label className="blog-meta-field__label" htmlFor="blog-status">Статус</label>
              <select id="blog-status" className="blog-meta-field__input" value={form.status === "published" ? "draft" : form.status} onChange={(event) => updateField("status", event.target.value)}>
                <option value="draft">Черновик</option>
                <option value="archived">Архив</option>
              </select>
              <div className="blog-meta-field__hint">Публикация выполняется отдельной кнопкой.</div>
            </div>
            <div className="blog-meta-field blog-meta-field--full">
              <label className="blog-meta-field__label">Теги</label>
              <div className="blog-tags-input">
                {form.tags.map((tag) => <span key={tag} className="blog-tag-chip">{tag}<button type="button" className="blog-tag-chip__remove" onClick={() => handleRemoveTag(tag)} aria-label={`Удалить тег ${tag}`}>x</button></span>)}
                <input className="blog-tags-input__field" value={tagValue} onChange={(event) => setTagValue(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === ",") { event.preventDefault(); handleAddTag(); } }} onBlur={handleAddTag} placeholder="аналитика, Ozon, CPC" />
              </div>
            </div>
            <div className="blog-meta-field blog-meta-field--full">
              <label className="blog-meta-field__label" htmlFor="blog-og-image">Изображение сниппета</label>
              <div className="blog-meta-field__row">
                <input id="blog-og-image" className="blog-meta-field__input" value={form.og_image} onChange={(event) => updateField("og_image", event.target.value)} placeholder="/static/blog/example.png" />
                <button type="button" className="admin-blog-button admin-blog-button--ghost" onClick={() => ogImageInputRef.current?.click()}>Загрузить</button>
              </div>
              <input ref={ogImageInputRef} type="file" accept="image/*" className="blog-meta-field__file" onChange={handleOgImageUpload} />
              <div className="blog-meta-field__hint">Миниатюра статьи, сниппет и изображение над первым H2. Рекомендуемый размер: 1200x630.</div>
            </div>
            <div className="seo-preview">
              <section className="seo-google"><p className="seo-google__label">Google snippet</p><p className="seo-google__url">{articleUrl}</p><p className="seo-google__title">{form.title || "Заголовок статьи"}</p><p className="seo-google__description">{previewDescription || "Краткое описание появится после заполнения description или контента."}</p></section>
              <section className="seo-og"><p className="seo-og__label">Social preview</p><div className="seo-og__card">{form.og_image ? <img className="seo-og__image" src={previewImage} alt="" /> : <div className="seo-og__image">1200 x 630</div>}<div className="seo-og__body"><p className="seo-og__site">Энилекс.Контроль</p><p className="seo-og__title">{form.title || "Заголовок статьи"}</p><p className="seo-og__description">{previewDescription || "Описание для социальных сетей появится здесь."}</p></div></div></section>
            </div>
            <div className="seo-indicators">
              <div className={`seo-indicator seo-indicator--${titleState}`}><span className="seo-indicator__dot" /><span>Title {titleState === "good" ? "в норме" : "нужно проверить"}</span></div>
              <div className={`seo-indicator seo-indicator--${descriptionState}`}><span className="seo-indicator__dot" /><span>Description {descriptionState === "good" ? "в норме" : "нужно проверить"}</span></div>
              <div className={`seo-indicator seo-indicator--${slugState}`}><span className="seo-indicator__dot" /><span>Slug {slugState === "good" ? "готов" : "обязателен"}</span></div>
            </div>
          </div> : null}
        </div>
      </>}
    </div>
  );
}

export default AdminBlogEditor;
